// Récapitulatif en lecture seule après soumission (contrat : RecapitulatifProps dans ./props.ts), alimenté par
// `reponses` du 200 de soumettre, ou du GET quand le statut est déjà `soumis`. Adapté du panneau admin
// « adminvisastravelapp/src/components/ReponsesFormulaire.tsx » (lot 5) : rien n'est évalué localement, on affiche
// ce que le backend a calculé à la dernière écriture (§3.3 du plan) — sections de `reponses.sections_visibles`,
// questions de `reponses.visibles` hors `info`, une ligne « libellé : valeur lisible » (libellé d'option, Oui / Non,
// date jj/mm/aaaa, tableau « a, b », fichiers → VignetteFichier : miniature ou icône typée + lien « nom_original », taille), « — » si vide.
// Mobile-first : libellé au-dessus de la valeur, retours à la ligne autorisés partout (aucun débordement à 375 px).
import React from 'react';
import { Box, Typography } from '@mui/material';
import type { RecapitulatifProps } from './props';
import type { FichierReponse, Question, Reponses, Section } from './types';
import { libelleQuestion, texteValeur } from './libelles';
import VignetteFichier from './VignetteFichier';
import { VERT, VERT_FONCE } from '../onboarding/tokens';

// Fichiers déposés pour une question (tableau vide si aucun)
const fichiersDe = (reponses: Reponses, id: string): FichierReponse[] => {
  const f = reponses.fichiers && reponses.fichiers[id];
  return Array.isArray(f) ? f : [];
};

// Une ligne « libellé : valeur lisible »
const Ligne: React.FC<{ question: Question; reponses: Reponses; jeton: string }> = ({ question: q, reponses, jeton }) => {
  const fichiers = q.type === 'fichier' ? fichiersDe(reponses, q.id) : [];
  const texte = q.type === 'fichier' ? '' : texteValeur(q, reponses.valeurs ? reponses.valeurs[q.id] : undefined);
  const renseigne = q.type === 'fichier' ? fichiers.length > 0 : texte !== '';

  let valeur: React.ReactNode;
  if (!renseigne) {
    valeur = (
      <Typography component="span" variant="body1" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>—</Typography>
    );
  } else if (q.type === 'fichier') {
    valeur = (
      <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5 }}>
        {fichiers.map((f) => (
          <VignetteFichier key={f.nom} jeton={jeton} fichier={f} taille={64} />
        ))}
      </Box>
    );
  } else {
    valeur = (
      <Typography component="span" variant="body1" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{texte}</Typography>
    );
  }

  return (
    <Box
      component="li"
      data-question={q.id}
      sx={{ listStyle: 'none', py: 1, borderBottom: '1px solid', borderColor: 'divider', '&:last-of-type': { borderBottom: 'none' } }}
    >
      <Typography component="span" variant="body2" display="block" sx={{ color: 'text.secondary', fontWeight: 500, mb: 0.25 }}>
        {libelleQuestion(q)}
      </Typography>
      <Box component="span" sx={{ display: 'block', minWidth: 0 }}>{valeur}</Box>
    </Box>
  );
};

const Recapitulatif: React.FC<RecapitulatifProps> = ({ definition, reponses, jeton }) => {
  const visibles = Array.isArray(reponses.visibles) ? reponses.visibles : [];
  const sectionsVisibles = Array.isArray(reponses.sections_visibles) ? reponses.sections_visibles : [];
  const toutesSections: Section[] = definition && Array.isArray(definition.sections) ? definition.sections : [];

  // Sections visibles avec leurs questions visibles (hors info) ; une section sans question à montrer est omise
  const sections = toutesSections
    .filter((s) => sectionsVisibles.indexOf(s.id) >= 0)
    .map((s) => ({
      section: s,
      questions: (Array.isArray(s.questions) ? s.questions : []).filter((q) => q.type !== 'info' && visibles.indexOf(q.id) >= 0),
    }))
    .filter((s) => s.questions.length > 0);

  if (sections.length === 0) {
    return (
      <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
        Aucune réponse à afficher
      </Typography>
    );
  }

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      {sections.map(({ section, questions }) => (
        <Box key={section.id} component="section" data-section={section.id} sx={{ mb: 3 }}>
          <Typography
            variant="subtitle1"
            component="h3"
            sx={{ fontWeight: 700, color: VERT_FONCE, borderLeft: `4px solid ${VERT}`, pl: 1.5, mb: 0.5, wordBreak: 'break-word' }}
          >
            {section.titre || section.id}
          </Typography>
          <Box component="ul" sx={{ m: 0, p: 0, pl: { xs: 0, sm: 2.5 } }}>
            {questions.map((q) => (
              <Ligne key={q.id} question={q} reponses={reponses} jeton={jeton} />
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default Recapitulatif;
